function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import queryString from "qs";
import QueryBuilder from "tg-client-query-builder";
import { uniqBy, uniq, get, clone, camelCase, startsWith, endsWith, last, orderBy, take, drop, isEmpty, isInteger } from "lodash";
import dayjs from "dayjs";
import { flatMap } from "lodash";
var defaultPageSizes = [5, 10, 15, 25, 50, 100, 200, 400];
export { defaultPageSizes };
export function getMergedOpts(topLevel, instanceLevel) {
  if (topLevel === void 0) {
    topLevel = {};
  }

  if (instanceLevel === void 0) {
    instanceLevel = {};
  }

  var merged = _extends({}, topLevel, instanceLevel);

  return _extends({
    formName: "tgDataTable"
  }, merged, {
    pageSize: merged.controlled_pageSize || merged.pageSize,
    defaults: _extends({
      pageSize: merged.controlled_pageSize || 25,
      order: [],
      //[-name, statusCode] //an array of camelCase display names with - sign to denote reverse
      searchTerm: "",
      page: 1,
      filters: [//filters look like this:
        // {
        //   selectedFilter: 'textContains', //camel case
        //   filterOn: ccDisplayName, //camel case display name
        //   filterValue: 'thomas',
        // }
      ]
    }, topLevel.defaults || {}, instanceLevel.defaults || {})
  });
}

function safeStringify(val) {
  if (val !== null && typeof val === "object") {
    return JSON.stringify(val);
  }

  return val;
}

function safeParse(val) {
  try {
    return JSON.parse(val);
  } catch (e) {
    return val;
  }
}

function getFieldsMappedByCCDisplayName(schema) {
  return schema.fields.reduce(function (acc, field) {
    acc[camelCase(field.displayName || field.path)] = field;
    return acc;
  }, {});
}

function orderEntitiesLocal(orderArray, entities, schema, ownProps) {
  if (orderArray && orderArray.length) {
    var orderFuncs = [];
    var ascOrDescArray = [];
    orderArray.forEach(function (order) {
      var ccDisplayName = order.replace(/^-/gi, "");
      var ccFields = getFieldsMappedByCCDisplayName(schema);
      var field = ccFields[ccDisplayName];

      if (!field) {
        throw new Error("Ruh roh, there should have been a column to sort on for " + order + "but none was found in " + schema.fields);
      }

      var path = field.path,
          getValueToFilterOn = field.getValueToFilterOn,
          sortFn = field.sortFn;

      if (field.type === "timestamp") {
        //with the timestamp logic below, make sure empty dates always end up on the bottom of the stack
        ascOrDescArray.push("desc");
      }

      ascOrDescArray.push(ccDisplayName === order ? "asc" : "desc"); //push the actual sorting function

      if (field.type === "timestamp") {
        //with the timestamp logic above, make sure empty dates always end up on the bottom of the stack
        orderFuncs.push(function (r) {
          var val = get(r, path);
          return !!val;
        });
      }

      if (path && endsWith(path.toLowerCase(), "id")) {
        orderFuncs.push(function (o) {
          return parseInt(get(o, path), 10);
        });
      } else if (sortFn) {
        var toOrder = Array.isArray(sortFn) ? sortFn : [sortFn];
        orderFuncs.push.apply(orderFuncs, toOrder);
      } else if (getValueToFilterOn) {
        orderFuncs.push(function (o) {
          return getValueToFilterOn(o, ownProps);
        });
      } else {
        orderFuncs.push(function (r) {
          var val = get(r, path);
          return val && val.toLowerCase ? val.toLowerCase() : val;
        });
      }
    });
    entities = orderBy(entities, orderFuncs, ascOrDescArray);
  }

  return entities;
}

function getAndAndOrFilters(allFilters) {
  var orFilters = [];
  var andFilters = [];
  var otherOrFilters = [];
  allFilters.forEach(function (filter) {
    if (filter.isOrFilter && typeof filter.filterValue === "string" && filter.filterValue.includes(",")) {
      // handle comma separated filters by adding more orWheres
      var allFilterValues = filter.filterValue.split(",");
      allFilterValues.forEach(function (filterValue, i) {
        filterValue = filterValue.trim();
        if (!filterValue) return;

        var newFilter = _extends({}, filter, {
          filterValue: filterValue
        });

        if (i === 0) {
          orFilters.push(newFilter);
        } else {
          var iMinus = i - 1;
          if (!otherOrFilters[iMinus]) otherOrFilters[iMinus] = [];
          otherOrFilters[iMinus].push(newFilter);
        }
      });
    } else if (filter.isOrFilter) {
      orFilters.push(filter);
    } else {
      andFilters.push(filter);
    }
  });
  return {
    orFilters: orFilters,
    andFilters: andFilters,
    otherOrFilters: otherOrFilters
  };
}

function filterEntitiesLocal(filters, searchTerm, entities, schema, ownProps) {
  if (filters === void 0) {
    filters = [];
  }

  var allFilters = getAllFilters(filters, searchTerm, schema);

  if (allFilters.length) {
    var ccFields = getFieldsMappedByCCDisplayName(schema);

    var _getAndAndOrFilters = getAndAndOrFilters(allFilters),
        andFilters = _getAndAndOrFilters.andFilters,
        orFilters = _getAndAndOrFilters.orFilters,
        otherOrFilters = _getAndAndOrFilters.otherOrFilters; //filter ands first


    andFilters.forEach(function (filter) {
      entities = getEntitiesForGivenFilter(entities, filter, ccFields, ownProps);
    }); //then filter ors

    if (orFilters.length) {
      var orEntities = [];
      orFilters.concat.apply(orFilters, otherOrFilters).forEach(function (filter) {
        orEntities = orEntities.concat(getEntitiesForGivenFilter(entities, filter, ccFields, ownProps));
      });
      entities = uniq(orEntities);
    }
  }

  return entities;
}

function cleanFilterValue(_filterValue, type) {
  var filterValue = _filterValue;

  if (type === "number" || type === "integer") {
    filterValue = Array.isArray(filterValue) ? filterValue.map(function (val) {
      return Number(val);
    }) : Number(filterValue);
  }

  return filterValue;
}

function getEntitiesForGivenFilter(entities, filter, ccFields, ownProps) {
  var filterOn = filter.filterOn,
      _filterValue = filter.filterValue,
      selectedFilter = filter.selectedFilter;
  var field = ccFields[filterOn];
  var path = field.path,
      getValueToFilterOn = field.getValueToFilterOn;
  var filterValue = cleanFilterValue(_filterValue, field.type);
  var subFilter = getSubFilter(false, selectedFilter, filterValue);
  entities = entities.filter(function (entity) {
    var fieldVal = getValueToFilterOn ? getValueToFilterOn(entity, ownProps) : get(entity, path);
    var shouldKeep = subFilter(fieldVal);
    return shouldKeep;
  });
  return entities;
}

function getFiltersFromSearchTerm(searchTerm, schema) {
  var searchTermFilters = [];

  if (searchTerm) {
    var sharedFields = {
      isOrFilter: true,
      isSearchTermFilter: true
    };
    schema.fields.forEach(function (field) {
      var type = field.type,
          displayName = field.displayName,
          path = field.path,
          searchDisabled = field.searchDisabled;
      if (searchDisabled || field.filterDisabled || type === "color") return;
      var nameToUse = camelCase(displayName || path);
      var filterValue = cleanFilterValue(searchTerm, type);

      if (type === "string" || type === "lookup") {
        searchTermFilters.push(_extends({}, sharedFields, {
          filterOn: nameToUse,
          filterValue: searchTerm,
          selectedFilter: "contains"
        }));
      } else if (type === "boolean") {
        var regex;

        try {
          regex = new RegExp("^" + searchTerm, "ig");
        } catch (error) {//ignore
        }

        if (regex) {
          if ("true".replace(regex, "") !== "true") {
            searchTermFilters.push(_extends({}, sharedFields, {
              filterOn: nameToUse,
              filterValue: true,
              selectedFilter: "true"
            }));
          } else if ("false".replace(regex, "") !== "false") {
            searchTermFilters.push(_extends({}, sharedFields, {
              filterOn: nameToUse,
              filterValue: false,
              selectedFilter: "false"
            }));
          }
        }
      } else if ((type === "number" || type === "integer") && !isNaN(filterValue)) {
        if (type === "integer" && !isInteger(filterValue)) {
          return;
        }

        searchTermFilters.push(_extends({}, sharedFields, {
          filterOn: nameToUse,
          filterValue: filterValue,
          selectedFilter: "equalTo"
        }));
      }
    });
  }

  return searchTermFilters;
}

function getSubFilter(qb, //if no qb is passed, it means we are filtering locally and want to get a function back that can be used in an array filter
selectedFilter, filterValue) {
  var ccSelectedFilter = camelCase(selectedFilter);
  var stringFilterValue = filterValue && filterValue.toString ? filterValue.toString() : filterValue;

  if (stringFilterValue === false) {
    // we still want to be able to search for the string "false" which will get parsed to false
    stringFilterValue = "false";
  } else {
    stringFilterValue = stringFilterValue || "";
  }

  var filterValLower = stringFilterValue.toLowerCase && stringFilterValue.toLowerCase();
  var arrayFilterValue = Array.isArray(filterValue) ? filterValue : stringFilterValue.split(".");

  if (ccSelectedFilter === "startsWith") {
    return qb ? qb.startsWith(stringFilterValue) //filter using qb (aka we're backend connected)
    : function (fieldVal) {
      //filter using plain old javascript (aka we've got a local table that isn't backend connected)
      if (!fieldVal || !fieldVal.toLowerCase) return false;
      return startsWith(fieldVal.toLowerCase(), filterValLower);
    };
  } else if (ccSelectedFilter === "endsWith") {
    return qb ? qb.endsWith(stringFilterValue) //filter using qb (aka we're backend connected)
    : function (fieldVal) {
      //filter using plain old javascript (aka we've got a local table that isn't backend connected)
      if (!fieldVal || !fieldVal.toLowerCase) return false;
      return endsWith(fieldVal.toLowerCase(), filterValLower);
    };
  } else if (ccSelectedFilter === "contains") {
    return qb ? qb.contains(stringFilterValue.replace(/_/g, "\\_")) //filter using qb (aka we're backend connected)
    : function (fieldVal) {
      //filter using plain old javascript (aka we've got a local table that isn't backend connected)
      if (!fieldVal || !fieldVal.toLowerCase) return false;
      return fieldVal.toLowerCase().replace(filterValLower, "") !== fieldVal.toLowerCase();
    };
  } else if (ccSelectedFilter === "inList") {
    return qb ? qb.inList(arrayFilterValue) //filter using qb (aka we're backend connected)
    : function (fieldVal) {
      //filter using plain old javascript (aka we've got a local table that isn't backend connected)
      if (!(fieldVal != null && fieldVal.toString)) return false;
      return arrayFilterValue.map(function (val) {
        return val && val.toLowerCase();
      }).indexOf(fieldVal.toString().toLowerCase()) > -1;
    };
  } else if (ccSelectedFilter === "notEmpty") {
    return qb ? [qb.notNull(), qb.notEquals("")] : function (fieldVal) {
      return !!fieldVal;
    };
  } else if (ccSelectedFilter === "isExactly") {
    return qb ? filterValue : function (fieldVal) {
      return fieldVal === filterValue;
    };
  } else if (ccSelectedFilter === "true") {
    return qb ? qb.equals(true) //filter using qb (aka we're backend connected)
    : function (fieldVal) {
      //filter using plain old javascript (aka we've got a local table that isn't backend connected)
      return !!fieldVal;
    };
  } else if (ccSelectedFilter === "false") {
    return qb ? qb.equals(false) //filter using qb (aka we're backend connected)
    : function (fieldVal) {
      //filter using plain old javascript (aka we've got a local table that isn't backend connected)
      return !fieldVal;
    };
  } else if (ccSelectedFilter === "isBetween") {
    return qb ? qb.between(new Date(arrayFilterValue[0]), new Date(arrayFilterValue[1])) : function (fieldVal) {
      return dayjs(arrayFilterValue[0]).valueOf() <= dayjs(fieldVal).valueOf() && dayjs(fieldVal).valueOf() <= dayjs(arrayFilterValue[1]).valueOf();
    };
  } else if (ccSelectedFilter === "isBefore") {
    return qb ? qb.lessThan(new Date(filterValue)) : function (fieldVal) {
      return dayjs(fieldVal).valueOf() < dayjs(filterValue).valueOf();
    };
  } else if (ccSelectedFilter === "isAfter") {
    return qb ? qb.greaterThan(new Date(filterValue)) : function (fieldVal) {
      return dayjs(fieldVal).valueOf() > dayjs(filterValue).valueOf();
    };
  } else if (ccSelectedFilter === "greaterThan") {
    return qb ? qb.greaterThan(filterValue) : function (fieldVal) {
      return fieldVal > filterValue;
    };
  } else if (ccSelectedFilter === "lessThan") {
    return qb ? qb.lessThan(filterValue) : function (fieldVal) {
      return fieldVal < filterValue;
    };
  } else if (ccSelectedFilter === "inRange") {
    return qb ? qb.between([filterValue[0], filterValue[1]]) : function (fieldVal) {
      return filterValue[0] <= fieldVal && fieldVal <= filterValue[1];
    };
  } else if (ccSelectedFilter === "equalTo") {
    return qb ? filterValue : function (fieldVal) {
      return fieldVal === filterValue;
    };
  }

  throw new Error("Unsupported filter " + selectedFilter + ". Please make a new filter if you need one");
}

export function getCurrentParamsFromUrl(location, isSimple) {
  var search = location.search;

  if (isSimple) {
    search = window.location.href.split("?")[1] || "";
  }

  return parseFilters(queryString.parse(search, {
    ignoreQueryPrefix: true
  }));
}
export function setCurrentParamsOnUrl(newParams, replace, isSimple) {
  var stringifiedFilters = stringifyFilters(newParams);
  var search = "?" + queryString.stringify(stringifiedFilters);

  if (isSimple) {
    return window.location.replace("" + window.location.href.split("?")[0] + search);
  }

  replace({
    search: search
  });
}

function stringifyFilters(newParams) {
  var filters;

  if (newParams.filters && newParams.filters.length) {
    filters = newParams.filters.reduce(function (acc, _ref, index) {
      var filterOn = _ref.filterOn,
          selectedFilter = _ref.selectedFilter,
          filterValue = _ref.filterValue;
      acc += (index > 0 ? "::" : "") + (filterOn + "__" + camelCase(selectedFilter) + "__" + safeStringify(Array.isArray(filterValue) ? filterValue.join(".") : filterValue));
      return acc;
    }, "");
  }

  var order;

  if (newParams.order && newParams.order.length) {
    order = newParams.order.reduce(function (acc, order, index) {
      acc += (index > 0 ? "___" : "") + order;
      return acc;
    }, "");
  }

  return _extends({}, newParams, {
    filters: filters,
    order: order
  });
}

function parseFilters(newParams) {
  return _extends({}, newParams, {
    order: newParams.order && newParams.order.split("___"),
    filters: newParams.filters && newParams.filters.split("::").map(function (filter) {
      var splitFilter = filter.split("__");
      var filterOn = splitFilter[0],
          selectedFilter = splitFilter[1],
          filterValue = splitFilter[2];
      return {
        filterOn: filterOn,
        selectedFilter: selectedFilter,
        filterValue: // for inList filters safeParse will convert two id values to a single number.
        // ex. "9.10" will become 9.1 which would completely mess up our filter
        selectedFilter === "inList" ? filterValue : safeParse(filterValue)
      };
    })
  });
}

function buildRef(qb, reference, searchField, expression) {
  var _qb$related$whereAny2;

  if (reference.reference) {
    var _qb$related$whereAny;

    // qb[reference.target] = {}
    return qb.related(reference.target).whereAny((_qb$related$whereAny = {}, _qb$related$whereAny[reference.sourceField] = buildRef(qb, reference.reference, searchField, expression), _qb$related$whereAny));
  }

  return qb.related(reference.target).whereAny((_qb$related$whereAny2 = {}, _qb$related$whereAny2[searchField] = expression, _qb$related$whereAny2));
}

export function makeDataTableHandlers(_ref2) {
  var setNewParams = _ref2.setNewParams,
      updateSearch = _ref2.updateSearch,
      defaults = _ref2.defaults,
      onlyOneFilter = _ref2.onlyOneFilter;

  //all of these actions have currentParams bound to them as their last arg in withTableParams
  function setSearchTerm(searchTerm, currentParams) {
    var newParams = _extends({}, currentParams, {
      page: undefined,
      //set page undefined to return the table to page 1
      searchTerm: searchTerm === defaults.searchTerm ? undefined : searchTerm
    });

    setNewParams(newParams);
    updateSearch(searchTerm);
    onlyOneFilter && clearFilters();
  }

  function addFilters(newFilters, currentParams) {
    if (!newFilters) return;
    var filters = uniqBy([].concat(newFilters, onlyOneFilter ? [] : currentParams.filters || []), "filterOn");

    var newParams = _extends({}, currentParams, {
      page: undefined,
      //set page undefined to return the table to page 1
      filters: filters
    });

    setNewParams(newParams);
    onlyOneFilter && updateSearch();
  }

  function removeSingleFilter(filterOn, currentParams) {
    var filters = currentParams.filters ? currentParams.filters.filter(function (filter) {
      return filter.filterOn !== filterOn;
    }) : undefined;

    var newParams = _extends({}, currentParams, {
      filters: filters
    });

    setNewParams(newParams);
  }

  function clearFilters(additionalFilterKeys) {
    if (additionalFilterKeys === void 0) {
      additionalFilterKeys = [];
    }

    var toClear = {
      filters: undefined,
      searchTerm: undefined,
      tags: undefined
    };
    additionalFilterKeys.forEach(function (key) {
      toClear[key] = undefined;
    });
    setNewParams(toClear);
    updateSearch();
  }

  function setPageSize(pageSize, currentParams) {
    var newParams = _extends({}, currentParams, {
      pageSize: pageSize === defaults.pageSize ? undefined : pageSize,
      page: undefined //set page undefined to return the table to page 1

    });

    setNewParams(newParams);
  }

  function setOrder(order, isRemove, shiftHeld, currentParams) {
    var newOrder = [];

    if (shiftHeld) {
      //first remove the old order
      newOrder = [].concat(currentParams.order || []).filter(function (value) {
        var shouldRemove = value.replace(/^-/, "") === order.replace(/^-/, "");
        return !shouldRemove;
      }); //then, if we are adding, pop the order onto the array

      if (!isRemove) {
        newOrder.push(order);
      }
    } else {
      if (isRemove) {
        newOrder = [];
      } else {
        newOrder = [order];
      }
    }

    var newParams = _extends({}, currentParams, {
      order: newOrder
    });

    setNewParams(newParams);
  }

  function setPage(page, currentParams) {
    var newParams = _extends({}, currentParams, {
      page: page === defaults.page ? undefined : page
    });

    setNewParams(newParams);
  }

  return {
    setSearchTerm: setSearchTerm,
    addFilters: addFilters,
    clearFilters: clearFilters,
    removeSingleFilter: removeSingleFilter,
    setPageSize: setPageSize,
    setPage: setPage,
    setOrder: setOrder,
    setNewParams: setNewParams
  };
} // if an inList value only has two items like
// 2.3 then it will get parsed to a number and
// break, convert it back to a string here

function cleanupFilter(filter) {
  var filterToUse = filter;

  if (filterToUse.selectedFilter === "inList" && typeof filterToUse.filterValue === "number") {
    filterToUse = _extends({}, filterToUse, {
      filterValue: filterToUse.filterValue.toString()
    });
  }

  if (filterToUse.selectedFilter === "inList") {
    filterToUse = _extends({}, filterToUse, {
      filterValue: filterToUse.filterValue.replace(/, |,/g, ".")
    });
  }

  return filterToUse;
}

function getAllFilters(filters, searchTerm, schema) {
  var allFilters = [].concat(filters, getFiltersFromSearchTerm(searchTerm, schema));
  allFilters = allFilters.filter(function (val) {
    return val !== "";
  }); //get rid of erroneous filters

  return allFilters.map(cleanupFilter);
}

export function getQueryParams(_ref3) {
  var currentParams = _ref3.currentParams,
      urlConnected = _ref3.urlConnected,
      defaults = _ref3.defaults,
      schema = _ref3.schema,
      isInfinite = _ref3.isInfinite,
      entities = _ref3.entities,
      isLocalCall = _ref3.isLocalCall,
      additionalFilter = _ref3.additionalFilter,
      additionalOrFilter = _ref3.additionalOrFilter,
      doNotCoercePageSize = _ref3.doNotCoercePageSize,
      noOrderError = _ref3.noOrderError,
      isCodeModel = _ref3.isCodeModel,
      ownProps = _ref3.ownProps;
  Object.keys(currentParams).forEach(function (key) {
    if (currentParams[key] === undefined) {
      delete currentParams[key]; //we want to use the default value if any of these are undefined
    }
  });

  var tableQueryParams = _extends({}, defaults, currentParams);

  var page = tableQueryParams.page,
      pageSize = tableQueryParams.pageSize,
      searchTerm = tableQueryParams.searchTerm,
      filters = tableQueryParams.filters,
      order = tableQueryParams.order;

  if (page <= 0 || isNaN(page)) {
    page = undefined;
  }

  if (isInfinite) {
    page = undefined;
    pageSize = undefined;
  }

  if (pageSize !== undefined && !doNotCoercePageSize) {
    //pageSize might come in as an unexpected number so we coerce it to be one of the nums in our pageSizes array
    var closest = clone(window.tgPageSizes || defaultPageSizes).sort(function (a, b) {
      return Math.abs(pageSize - a) - Math.abs(pageSize - b);
    })[0];
    pageSize = closest;
  }

  var toReturn = {
    //these are values that might be generally useful for the wrapped component
    page: page,
    pageSize: ownProps.controlled_pageSize || pageSize,
    order: order,
    filters: filters,
    searchTerm: searchTerm
  };

  if (isLocalCall) {
    var newEntities = entities; //if the table is local (aka not directly connected to a db) then we need to
    //handle filtering/paging/sorting all on the front end

    newEntities = filterEntitiesLocal(filters, searchTerm, newEntities, schema, ownProps);
    newEntities = orderEntitiesLocal(order, newEntities, schema, ownProps);
    var entitiesAcrossPages = newEntities;
    var newEntityCount = newEntities.length; //calculate the sorted, filtered, paged entities for the local table

    if (!isInfinite && !ownProps.controlled_pageSize) {
      var offset = (page - 1) * pageSize;
      newEntities = take(drop(newEntities, offset), pageSize);
    }

    toReturn.entities = newEntities;
    toReturn.entitiesAcrossPages = entitiesAcrossPages;
    toReturn.entityCount = newEntityCount; //if this call is being made by a local-data only connected datatable component,
    //we don't want to do the following gql stuff

    return toReturn;
  } else {
    var graphqlQueryParams = {
      // need to make sure sort exists because of https://github.com/apollographql/apollo-client/issues/3077
      sort: []
    };

    if (isInfinite) {
      graphqlQueryParams.pageSize = 999;
      graphqlQueryParams.pageNumber = 1;
    } else {
      graphqlQueryParams.pageNumber = Number(page);
      graphqlQueryParams.pageSize = ownProps.controlled_pageSize || Number(pageSize);
    }

    var model = schema.model;
    var qb = new QueryBuilder(model); // qb = qb.filter('user')
    // qb = qb.whereAny({
    //   userStatus: qb.related('userStatus').whereAny({
    //     code: qb.contains('pending')
    //   })
    // })
    // qb = qb.andWhere({
    //   age: qb.lessThan(12)
    // })
    // qb.toJSON()
    // let filterBuilder = qb.filter(model); //start filter on model

    var ccFields = getFieldsMappedByCCDisplayName(schema);

    if (tableQueryParams.order && tableQueryParams.order.length) {
      tableQueryParams.order.forEach(function (orderVal) {
        var ccDisplayName = orderVal.replace(/^-/gi, "");
        var schemaForField = ccFields[ccDisplayName];

        if (schemaForField) {
          var path = schemaForField.path;
          var reversed = ccDisplayName !== orderVal;
          var prefix = reversed ? "-" : "";
          graphqlQueryParams.sort = [].concat(graphqlQueryParams.sort || [], [prefix + path]);
        } else {
          !noOrderError && console.error("No schema for field found!", ccDisplayName, schema.fields);
        }
      });
    }

    var errorParsingUrlString;
    var additionalFilterToUse = additionalFilter(qb, currentParams);
    var additionalOrFilterToUse = additionalOrFilter(qb, currentParams);

    if (additionalOrFilterToUse && additionalOrFilterToUse.ignoreSearchTerm) {
      searchTerm = "";
      additionalOrFilterToUse = additionalOrFilterToUse.additionalOrFilterToUse;
    }

    var allFilters = getAllFilters(filters, searchTerm, schema);

    var _getAndAndOrFilters2 = getAndAndOrFilters(allFilters),
        andFilters = _getAndAndOrFilters2.andFilters,
        orFilters = _getAndAndOrFilters2.orFilters,
        otherOrFilters = _getAndAndOrFilters2.otherOrFilters;

    try {
      var flattenFilters = function flattenFilters(filterObj) {
        return flatMap(Object.keys(filterObj), function (key) {
          return filterObj[key].map(function (filter) {
            var _ref4;

            return _ref4 = {}, _ref4[key] = filter, _ref4;
          });
        });
      };

      var orFiltersObject = getQueries(orFilters, qb, ccFields);
      var allOrFilters = flattenFilters(orFiltersObject);
      otherOrFilters.forEach(function (orFilters) {
        var otherOrFiltersObject = getQueries(orFilters, qb, ccFields);
        allOrFilters = allOrFilters.concat(flattenFilters(otherOrFiltersObject));
      });
      allOrFilters.push(additionalOrFilterToUse);
      allOrFilters = allOrFilters.filter(function (obj) {
        return !isEmpty(obj);
      });
      var unflattenedAndQueries = getQueries(andFilters, qb, ccFields);
      var allAndFilters = flattenFilters(unflattenedAndQueries);
      allAndFilters.push(additionalFilterToUse);
      allAndFilters = allAndFilters.filter(function (obj) {
        return !isEmpty(obj);
      });

      if (allAndFilters.length) {
        qb.whereAll.apply(qb, allAndFilters);
      }

      if (allOrFilters.length) {
        qb.andWhereAny.apply(qb, allOrFilters);
      }

      var columnCustomFilters = getColumnCustomFilters(andFilters, qb, ccFields);

      if (columnCustomFilters.length) {
        qb.whereAll.apply(qb, columnCustomFilters);
      }
    } catch (e) {
      if (urlConnected) {
        errorParsingUrlString = e;
        console.error("The following error occurred when trying to build the query params. This is probably due to a malformed URL:", e);
      } else {
        console.error("Error building query params from filter:");
        throw e;
      }
    }

    if (qb.query.filters.length) {
      graphqlQueryParams.filter = qb.toJSON();
    } // by default make sort by updated at


    if (!graphqlQueryParams.sort.length) {
      graphqlQueryParams.sort.push("-updatedAt");
    } // in case entries that have the same value in the column being sorted on
    // fall back to id as a secondary sort to make sure ordering happens correctly


    graphqlQueryParams.sort.push(isCodeModel ? "code" : window.__sortId || "id");
    return _extends({}, toReturn, {
      //the query params will get passed directly to as variables to the graphql query
      variables: graphqlQueryParams,
      errorParsingUrlString: errorParsingUrlString
    });
  }
}

function getSubFiltersAndPath(filter, qb, ccFields) {
  var selectedFilter = filter.selectedFilter,
      filterValue = filter.filterValue,
      filterOn = filter.filterOn;
  var fieldSchema = ccFields[filterOn];
  var filterValueToUse = filterValue;

  if (fieldSchema) {
    if (fieldSchema.normalizeFilter) {
      filterValueToUse = fieldSchema.normalizeFilter(filterValue, selectedFilter, filterOn);
    }
  }

  var _subFilters = getSubFilter(qb, selectedFilter, filterValueToUse);

  var filterField;

  if (fieldSchema) {
    var path = fieldSchema.path,
        reference = fieldSchema.reference;

    if (reference) {
      filterField = reference.sourceField;
    } else {
      filterField = path;
    }
  } else if (filterOn === "id") {
    filterField = filterOn;
  } else {
    console.error("Trying to filter on unknown field");
  }

  var subFiltersToUse = [];
  var subFilters = Array.isArray(_subFilters) ? _subFilters : [_subFilters];
  subFilters.forEach(function (subFilter) {
    var subFilterToUse = subFilter;

    if (fieldSchema) {
      var _path = fieldSchema.path,
          _reference = fieldSchema.reference;

      if (_reference) {
        subFilterToUse = buildRef(qb, _reference, last(_path.split(".")), subFilter);
      }
    }

    subFiltersToUse.push(subFilterToUse);
  });
  return {
    path: filterField,
    subFilters: subFiltersToUse
  };
}

function getQueries(filters, qb, ccFields) {
  var subQueries = filters.reduce(function (acc, filter) {
    if (!filter) {
      console.warn("We should always have a filter object!");
      return acc;
    }

    var filterOn = filter.filterOn;
    var fieldSchema = ccFields[filterOn]; // will be handled below

    if (!filter.isSearchTermFilter && fieldSchema != null && fieldSchema.additionalColumnFilter) return acc;

    var _getSubFiltersAndPath = getSubFiltersAndPath(filter, qb, ccFields),
        path = _getSubFiltersAndPath.path,
        subFilters = _getSubFiltersAndPath.subFilters;

    acc[path] = subFilters;
    return acc;
  }, {});
  return subQueries;
}

function getColumnCustomFilters(filters, qb, ccFields) {
  var subQueries = filters.reduce(function (acc, filter) {
    if (!filter) {
      console.warn("We should always have a filter object!");
      return acc;
    }

    var filterOn = filter.filterOn;
    var fieldSchema = ccFields[filterOn];

    if (filter.isSearchTermFilter || !(fieldSchema != null && fieldSchema.additionalColumnFilter)) {
      return acc;
    }

    var _getSubFiltersAndPath2 = getSubFiltersAndPath(filter, qb, ccFields),
        path = _getSubFiltersAndPath2.path,
        subFilters = _getSubFiltersAndPath2.subFilters;
    /* the column filters need to have access to this sub filter but also be able to add additional
     filter logic.
    ex.
    qb.whereAny({
      id: qb.related("extendedStringValueView.buildSampleId").whereAll({
          value: "something",
          extendedPropertyId: "myId"
        })
      ...
      })
       is possible because the returned accumulator will be passed to whereAny
    */


    subFilters.forEach(function (subFilter) {
      acc.push(fieldSchema.additionalColumnFilter(qb, subFilter, path));
    });
    return acc;
  }, []);
  return subQueries;
}